import { useEffect } from "react";

/**
 * SPA meta-tag updater. Keeps the SPA crawlable in modern search engines
 * (Google renders JS). For SSR-perfect SEO, migrate to Next.js later.
 */
function upsertMeta({ name, property: prop, content }) {
  if (!content) return;
  const sel = name ? `meta[name="${name}"]` : `meta[property="${prop}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (prop) el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  const existing = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (existing) existing.remove();
  if (!data) return;
  const el = document.createElement("script");
  el.setAttribute("type", "application/ld+json");
  el.setAttribute("data-jsonld", id);
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

export default function SEO({
  title,
  description,
  ogImage,
  canonical,
  keywords,
  jsonLd, // single or array
  lang = "en",
}) {
  useEffect(() => {
    if (title) document.title = title;
    document.documentElement.setAttribute("lang", lang);
    upsertMeta({ name: "description", content: description });
    if (keywords) upsertMeta({ name: "keywords", content: keywords });
    upsertMeta({ property: "og:title", content: title });
    upsertMeta({ property: "og:description", content: description });
    upsertMeta({ property: "og:type", content: "website" });
    if (ogImage) upsertMeta({ property: "og:image", content: ogImage });
    upsertMeta({ name: "twitter:card", content: ogImage ? "summary_large_image" : "summary" });
    upsertMeta({ name: "twitter:title", content: title });
    upsertMeta({ name: "twitter:description", content: description });
    if (ogImage) upsertMeta({ name: "twitter:image", content: ogImage });
    upsertLink("canonical", canonical || window.location.href.split("?")[0]);
    upsertMeta({ property: "og:url", content: canonical || window.location.href });

    const list = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    list.forEach((d, i) => upsertJsonLd(`page-${i}`, d));
    // cleanup jsonld tags added by prior pages
    document.head.querySelectorAll("script[data-jsonld]").forEach(el => {
      const idx = parseInt(el.dataset.jsonld.replace("page-", ""));
      if (idx >= list.length) el.remove();
    });
  }, [title, description, ogImage, canonical, keywords, jsonLd, lang]);

  return null;
}

/** Convenience: default site-wide LocalBusiness + Organization JSON-LD */
export const localBusinessJsonLd = ({ name, phone, email, url, image }) => ({
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  name,
  telephone: phone,
  email,
  url,
  image,
  address: { "@type": "PostalAddress", addressRegion: "Maharashtra", addressCountry: "IN" },
  areaServed: [{ "@type": "State", name: "Maharashtra" }],
  priceRange: "₹₹",
});
